import type { ReactNode } from "react";

import { Mark } from "./Mark";

/* ------------------------------------------------------------------ status */

export type StatusTone = "good" | "warning" | "serious" | "critical" | "neutral";

const TONE_STYLE: Record<StatusTone, { dot: string; text: string }> = {
  good: { dot: "bg-good", text: "text-ink" },
  warning: { dot: "bg-warning", text: "text-ink" },
  serious: { dot: "bg-serious", text: "text-ink" },
  critical: { dot: "bg-critical", text: "text-ink" },
  neutral: { dot: "bg-baseline", text: "text-ink-2" },
};

/**
 * Status is never carried by colour alone — every badge ships a dot *and* a
 * label, which is also what keeps it readable in forced-colours mode.
 *
 * **A dot and a word, and no outline.** It was a pill with a border round
 * both, which on the Engagements list was forty-seven outlined shapes saying
 * what forty-seven dots already said. The outline separated nothing.
 */
export function StatusBadge({
  tone,
  children,
}: {
  tone: StatusTone;
  children: ReactNode;
}) {
  const style = TONE_STYLE[tone];
  return (
    <span className={`inline-flex items-center gap-1.5 text-xs font-medium ${style.text}`}>
      <span className={`size-1.5 shrink-0 rounded-full ${style.dot}`} aria-hidden />
      {children}
    </span>
  );
}

export function runTone(status: string): StatusTone {
  switch (status) {
    case "completed":
      return "good";
    case "running":
      return "warning";
    case "queued":
      // Admitted and paid for, waiting for a slot — not a problem
      // state, so it must not read as one.
      return "neutral";
    case "failed":
      return "critical";
    case "cancelled":
      // Somebody chose to stop it. Red beside a choice reports a fault
      // that did not happen, and the counts above the list never coloured
      // it — the two places disagreed about the same run.
      return "neutral";
    case "rescheduling":
      return "serious";
    default:
      return "neutral";
  }
}

/* -------------------------------------------------------------------- card */

export function Card({
  title,
  action,
  children,
  className = "",
}: {
  title?: ReactNode;
  action?: ReactNode;
  children: ReactNode;
  className?: string;
}) {
  return (
    <section className={`card ${className}`}>
      {(title || action) && (
        <header className="flex items-center justify-between gap-3 border-b border-line px-4 py-3">
          {typeof title === "string" ? (
            <h2 className="text-sm font-semibold">{title}</h2>
          ) : (
            title
          )}
          {action}
        </header>
      )}
      {children}
    </section>
  );
}

/* --------------------------------------------------------------------- tag */

/**
 * A fact about a thing, in the brand's label: mono, uppercase, in a box with
 * the small radius. GATE on a phase, PRIVATE on a playbook.
 *
 * There were five shapes for this — a round pill in the sans, a square box in
 * the sans, a round box in the mono, a square box in a smaller mono, and the
 * tile's audience pills — each drawn where it was needed. A status is a dot
 * and a word (`StatusBadge`); everything else that annotates is a `Tag`.
 * `strong` is the one that carries weight without a colour.
 */
export function Tag({
  children,
  strong = false,
  title,
  className = "",
}: {
  children: ReactNode;
  strong?: boolean;
  title?: string;
  className?: string;
}) {
  return (
    <span
      title={title}
      className={`label inline-flex shrink-0 items-center gap-1 rounded-sm border px-1.5 ${
        strong ? "border-line-strong text-ink" : "border-line"
      } ${className}`}
    >
      {children}
    </span>
  );
}

/* ------------------------------------------------------------------ button */

export type ButtonVariant = "primary" | "secondary" | "danger" | "ghost";
export type ButtonSize = "md" | "sm";

/**
 * A button's look, for the places a link has to wear it — "New engagement" is
 * a navigation, so it is an anchor, and it used to restate these classes by
 * hand. That copy is where the primary label was a fixed `text-white`, which
 * in dark mode sat on an `accent` that is white too (#124). The label is
 * `on-accent` now, the ink `build_tokens.py` decides is readable on the
 * ground, and there is one copy of it.
 */
export function buttonClasses(
  variant: ButtonVariant = "secondary",
  size: ButtonSize = "md",
): string {
  const base =
    "inline-flex shrink-0 items-center justify-center gap-2 rounded-lg font-medium transition-colors disabled:cursor-not-allowed disabled:opacity-50";
  //     Two heights, from the master: `md` for a form or a page's actions,
  //     `sm` for a row or the bar. Each caller used to pass its own `py-1`.
  const sizes = {
    md: "h-control px-3 text-sm",
    sm: "h-control-sm px-2.5 text-xs",
  } as const;
  const variants = {
    primary: "bg-accent text-on-accent hover:opacity-90",
    secondary: "border border-line-strong bg-surface hover:bg-surface-2",
    danger: "border border-critical text-critical-text hover:bg-critical/10",
    ghost: "text-ink-2 hover:bg-surface-2 hover:text-ink",
  } as const;
  return `${base} ${sizes[size]} ${variants[variant]}`;
}

export function Button({
  variant = "secondary",
  size = "md",
  className = "",
  ...props
}: React.ButtonHTMLAttributes<HTMLButtonElement> & {
  variant?: ButtonVariant;
  size?: ButtonSize;
}) {
  return <button className={`${buttonClasses(variant, size)} ${className}`} {...props} />;
}

/* ------------------------------------------------------------------- state */

export function Empty({ title, hint }: { title: string; hint?: ReactNode }) {
  return (
    <div className="flex flex-col items-center gap-2 px-6 py-14 text-center">
      <p className="text-sm font-medium text-ink-2">{title}</p>
      {hint && <p className="max-w-md text-sm text-muted">{hint}</p>}
    </div>
  );
}

/**
 * Something is being loaded or written, and somebody is waiting for it.
 *
 * **The mark, drawing itself, not a spinning circle** (#136). `Mark`'s own
 * rule for its loop is *somebody is waiting*, and this is the one place every
 * wait in the app goes through — so a page loading, a summary being written
 * and a statement being drafted all show the same thing the front door does,
 * from one component. 44px, the size the discussion and the upload already
 * wait at: above 18 the mark animates, below it the reduction drops the tier
 * the drawing is about. Mounted with the wait and gone with it, so every wait
 * starts the drawing again.
 */
export function Spinner({ label = "Loading" }: { label?: string }) {
  return (
    <div
      className="flex flex-col items-center justify-center gap-3 px-6 py-14 text-sm text-muted"
      role="status"
    >
      <Mark size={44} animate loop />
      {label}
    </div>
  );
}

export function ErrorNote({ error }: { error: unknown }) {
  const message = error instanceof Error ? error.message : String(error);
  return (
    <Notice tone="critical">
      <strong className="font-semibold">Error</strong> — {message}
    </Notice>
  );
}

/**
 * Something the reader should know about, in a box: why a run failed, a
 * document that is still locked, a migration the graph is waiting for.
 *
 * **The tone is a dot; the words are ink.** About fifteen of these were
 * written in the brand's orange and a few in red, on orange and red washes —
 * so "the service is starting up" wore the colour reserved for the one finding
 * on a page, and the text on the orange wash measured 4.37:1. `attention` is
 * the orange, for the one thing a reader has to act on; a failure is
 * `critical`; anything else is `warning` or `neutral`.
 */
export type NoteTone = "critical" | "warning" | "attention" | "neutral";

const NOTE_DOT: Record<NoteTone, string> = {
  critical: "bg-critical",
  warning: "bg-warning",
  attention: "bg-signal",
  neutral: "bg-baseline",
};

export function Notice({
  tone,
  title,
  children,
  compact = false,
  className = "",
}: {
  tone: NoteTone;
  title?: ReactNode;
  children?: ReactNode;
  /** Inside a form or a panel, where the note is smaller than the page's. */
  compact?: boolean;
  className?: string;
}) {
  const ground =
    tone === "critical" ? "border-critical/40 bg-critical/5" : "border-line bg-surface-2";
  const size = compact ? "px-3 py-2.5 text-xs" : "px-4 py-3 text-sm";
  return (
    <div
      role={tone === "critical" ? "alert" : undefined}
      className={`flex items-start gap-2.5 rounded-lg border text-ink ${size} ${ground} ${className}`}
    >
      <span
        className={`${compact ? "mt-1" : "mt-1.5"} size-1.5 shrink-0 rounded-full ${NOTE_DOT[tone]}`}
        aria-hidden
      />
      <div className="min-w-0 space-y-1">
        {title && <p className="font-medium">{title}</p>}
        {children}
      </div>
    </div>
  );
}

/** The same, as one line under a field or beside a control. */
export function InlineNote({
  tone,
  children,
  className = "",
}: {
  tone: NoteTone;
  children: ReactNode;
  className?: string;
}) {
  return (
    <p
      role={tone === "critical" ? "alert" : undefined}
      className={`flex items-baseline gap-2 text-xs text-ink ${className}`}
    >
      <span
        className={`size-1.5 shrink-0 -translate-y-px rounded-full ${NOTE_DOT[tone]}`}
        aria-hidden
      />
      <span className="min-w-0">{children}</span>
    </p>
  );
}
